import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Crosshair, MapPin, Search, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useEvents } from '@/context/EventContext';
import { useCity } from '@/hooks/useCatalog';
import { distanceKm } from '@/lib/catalog';
import { Shimmer } from '@/components/ui';

/**
 * City picker. The cities are LK Admin's zones (Settings → Zones), so adding
 * Jodhpur there is all it takes for it to appear here — this used to be a
 * hardcoded list of ~800 towns, most of which LocalKonnect doesn't serve, and
 * picking one only changed the header label.
 *
 * Phones get a bottom sheet (thumb reach); larger screens a centred dialog.
 */
const LocationModal = ({ isOpen, onClose }) => {
  const { setSelectedCity } = useEvents();
  const { city: current, cities, loading } = useCity();
  const [term, setTerm] = useState('');
  const [detecting, setDetecting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    setTerm('');
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const nameOf = (zoneId) => cities.find((c) => c.zoneId === zoneId)?.name;

  const visible = useMemo(() => {
    const q = term.trim().toLowerCase();
    return q ? cities.filter((c) => `${c.name} ${c.state}`.toLowerCase().includes(q)) : cities;
  }, [cities, term]);

  const choose = (city) => {
    setSelectedCity({ zoneId: city.zoneId, name: city.name, lat: city.lat, lng: city.lng });
    onClose();
  };

  /** Nearest served city to where the visitor actually is. */
  const detectCity = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser can't share your location");
      return;
    }
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setDetecting(false);
        const ranked = cities
          .filter((c) => c.lat != null && c.lng != null)
          .map((c) => ({ city: c, km: distanceKm(coords.latitude, coords.longitude, c.lat, c.lng) }))
          .filter((r) => r.km != null)
          .sort((a, b) => a.km - b.km);
        if (!ranked.length) {
          toast.error("We couldn't match your location to a city");
          return;
        }
        const { city, km } = ranked[0];
        choose(city);
        // Be honest when "nearest" is still far away.
        toast(
          km > 60
            ? `We're not in your area yet — showing ${city.name}, the nearest city we serve (${Math.round(km)} km).`
            : `Showing events in ${city.name}`,
          { icon: '📍' }
        );
      },
      (err) => {
        setDetecting(false);
        toast.error(err.code === 1 ? 'Location permission was denied' : "We couldn't get your location");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="city-picker-title"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'tween', duration: 0.2 }}
            className="flex max-h-[88dvh] w-full flex-col rounded-t-3xl bg-white shadow-2xl sm:max-w-lg sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grab handle — phones read a bottom sheet by it */}
            <div className="flex justify-center pt-3 sm:hidden" aria-hidden="true">
              <span className="h-1.5 w-10 rounded-full bg-ink-200" />
            </div>

            <div className="flex items-center justify-between px-5 pb-3 pt-3 sm:pt-5">
              <h2 id="city-picker-title" className="text-h3 text-ink-900">
                Choose your city
              </h2>
              <button type="button" onClick={onClose} aria-label="Close" className="lk-icon-btn border-transparent">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 px-5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                {/* 16px: iOS zooms the page on focus below that. */}
                <input
                  type="search"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder="Search cities"
                  aria-label="Search cities"
                  className="lk-focus w-full rounded-full border border-ink-200 bg-surface-muted py-3 pl-10 pr-4 text-base outline-none focus:border-brand-secondary focus:bg-white md:text-sm"
                />
              </div>
              <button
                type="button"
                onClick={detectCity}
                disabled={detecting || loading || cities.length === 0}
                className="lk-focus flex min-h-[44px] w-full items-center gap-2 rounded-xl px-2 text-sm font-semibold text-brand-secondary hover:bg-brand-tint disabled:opacity-50"
              >
                {detecting ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : (
                  <Crosshair className="h-4 w-4" />
                )}
                {detecting ? 'Finding the nearest city…' : 'Use my current location'}
              </button>
            </div>

            <div className="mt-2 flex-1 overflow-y-auto px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
              <p className="lk-eyebrow mb-2">Cities we serve</p>
              {loading ? (
                <div className="grid grid-cols-2 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Shimmer key={i} className="h-20 rounded-2xl" />
                  ))}
                </div>
              ) : cities.length === 0 ? (
                <p className="rounded-2xl bg-surface-muted p-4 text-sm text-ink-500">
                  We couldn't load cities right now. Please try again in a moment.
                </p>
              ) : visible.length === 0 ? (
                <p className="rounded-2xl bg-surface-muted p-4 text-sm text-ink-500">
                  We're not in "{term}" yet — more cities are coming soon.
                </p>
              ) : (
                <ul className="grid grid-cols-2 gap-3">
                  {visible.map((c) => {
                    const selected = current?.zoneId === c.zoneId;
                    const borrowed = c.alsoShowZoneIds.map(nameOf).filter(Boolean);
                    const hint =
                      c.providerCount > 0
                        ? `${c.providerCount} venue${c.providerCount === 1 ? '' : 's'}`
                        : borrowed.length
                          ? `Events from ${borrowed.join(', ')}`
                          : 'Coming soon';
                    return (
                      <li key={c.zoneId}>
                        <button
                          type="button"
                          onClick={() => choose(c)}
                          aria-pressed={selected}
                          className={`lk-focus flex h-full min-h-[80px] w-full items-start gap-2.5 rounded-2xl border p-3.5 text-left transition-colors active:scale-[0.98] ${
                            selected
                              ? 'border-brand-secondary bg-brand-tint'
                              : 'border-ink-200 hover:border-ink-400 hover:bg-surface-muted'
                          }`}
                        >
                          <MapPin
                            className={`mt-0.5 h-4 w-4 flex-none ${selected ? 'text-brand-secondary' : 'text-ink-400'}`}
                            aria-hidden="true"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1 font-semibold text-ink-900">
                              <span className="truncate">{c.name}</span>
                              {selected && <Check className="h-4 w-4 flex-none text-brand-secondary" aria-label="Selected" />}
                            </span>
                            {c.state && <span className="block truncate text-xs text-ink-500">{c.state}</span>}
                            <span className="mt-0.5 block truncate text-xs text-ink-500">{hint}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="mt-4 text-center text-xs text-ink-500">More cities are coming soon.</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LocationModal;
